import { useEffect, useState } from 'react';
import { parseUnits, erc20Abi, isAddress, formatUnits } from 'viem';
import { useAccount, useBalance, usePublicClient, useReadContract } from 'wagmi';
import { toast } from 'react-toastify';
import { Web3Button } from './Web3Button';
import { rewardTokenAddress, rewardTokenSymbol } from './Home';
import { RewardTokenAbi } from './abi/abis';

type Address = `0x${string}`;

export default function AddLiquidity() {
  const { address } = useAccount();
  const client = usePublicClient();

  const [dexRouter, setDexRouter] = useState<Address>('0x165C3410fC91EF562C50559f7d2289fEbed552d9'); // PulseX v2 Router
  // const [tokenA, setTokenA] = useState<Address>('0x0000000000000000000000000000000000000000');
  const [tokenAAmount, setTokenAAmount] = useState('0');
  const [tokenBAddress, setTokenBAddress] = useState<Address>('0xA1077a294dDE1B09bB078844df40758a5D0f9a27');
  const [tokenBAmount, setTokenBAmount] = useState('0');
  const [tokenBSymbol, setTokenBSymbol] = useState('?');
  const [tokenBDecimals, setTokenBDecimals] = useState(18);
  const [slippage, setSlippage] = useState('0.5');
  const [recipient, setRecipient] = useState<Address>(address || '0x0000000000000000000000000000000000000000');
  const [deadline, setDeadline] = useState('20'); // 20 minutes from now

  useEffect(() => {
    async function fetchErc20Metadata() {
      try {
        const [symbol, decimals] = await Promise.all([
          client!.readContract({
            address: tokenBAddress,
            abi: erc20Abi,
            functionName: 'symbol',
          }),
          client!.readContract({
            address: tokenBAddress,
            abi: erc20Abi,
            functionName: 'decimals',
          }),
        ])
        setTokenBSymbol(symbol);
        setTokenBDecimals(decimals);
      } catch (err) {
        console.error(err);
        setTokenBSymbol('?');
        setTokenBDecimals(18);
      }
    }
    if (client && tokenBAddress)
      fetchErc20Metadata();
  }, [client, tokenBAddress]);

  const { data: tokenABalanceData } = useBalance({
    address,
    token: rewardTokenAddress,
  })

  const { data: tokenBBalanceData } = useBalance({
    address,
    token: tokenBAddress,
  })

  let tokenAAmountRaw: bigint | undefined;
  try { tokenAAmountRaw = parseUnits(tokenAAmount, 18); } catch { }
  let tokenBAmountRaw: bigint | undefined;
  try { tokenBAmountRaw = parseUnits(tokenBAmount, tokenBDecimals); } catch { }

  const { data: tokenAAllowance, refetch: tokenAAllowanceRefetch } = useReadContract({
    address: rewardTokenAddress,
    abi: erc20Abi,
    functionName: "allowance",
    args: [address!, rewardTokenAddress],
    query: {
      enabled: !!address,
      refetchInterval: 3000,
    },
  } as const);

  const { data: tokenBAllowance, refetch: tokenBAllowanceRefetch } = useReadContract({
    address: tokenBAddress,
    abi: erc20Abi,
    functionName: "allowance",
    args: [address!, rewardTokenAddress],
    query: {
      enabled: !!address && isAddress(tokenBAddress),
    },
  } as const);

  const calculateMinAmount = (amountBN: bigint, slippage: string) => {
    const slippageBps = BigInt(Math.round(parseFloat(slippage) * 100));
    const slippageAmount = (amountBN * slippageBps) / 10000n;
    return amountBN - slippageAmount;
  };

  const tokenAApprovalSufficient = tokenAAllowance !== undefined && tokenAAmountRaw !== undefined && tokenAAllowance >= tokenAAmountRaw;
  const tokenBApprovalSufficient = tokenBAllowance !== undefined && tokenBAmountRaw !== undefined && tokenBAllowance >= tokenBAmountRaw;

  const { data: estimateData, error: estimateError } = useReadContract(
    {
      account: address,
      address: rewardTokenAddress,
      abi: RewardTokenAbi,
      functionName: 'addLiquidity',
      args: [
        dexRouter,
        tokenAAmountRaw,
        tokenBAddress,
        tokenBAmountRaw,
        0,
        0,
        recipient,
        999999999999999999999n,
      ],
      query: {
        enabled: tokenAApprovalSufficient && tokenBApprovalSufficient,
        refetchInterval: 3000,
      }
    } as const
  );

  const validInput = tokenAApprovalSufficient && tokenBApprovalSufficient && tokenAAmountRaw !== undefined && tokenAAmountRaw >= 0n &&
    tokenBApprovalSufficient && tokenBAmountRaw !== undefined && tokenBAmountRaw > 0n &&
    isAddress(dexRouter) && isAddress(tokenBAddress) && isAddress(recipient) &&
    parseInt(deadline) > 0 && parseFloat(slippage) >= 0 &&
    estimateData !== undefined;

  return (
    <div className="border-2 border-green-900 bg-gradient-to-b from-green-950 p-3 rounded-xl">
      <div className="grid grid-cols-[auto_auto] gap-2 items-center">
        <div className="col-span-2 flex flex-row">
          <div className="label text-bold text-xl grow">Add V2 Liquidity Without Fees</div>
        </div>

        <div className="label">V2 DEX router address:</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-[25rem]" type="text" value={dexRouter} onChange={(e) => setDexRouter(e.target.value as Address)} />
          {!(isAddress(dexRouter)) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="col-span-2 label">{rewardTokenSymbol} token:</div>

        <div className="label ml-4">Address:</div>
        <input className="input w-[25rem]" type="text" value={rewardTokenAddress} disabled />

        <div className="label ml-4">Current balance:</div>
        <input className="input w-[25rem] " name="current-balance" type="text" value={tokenABalanceData?.formatted} disabled />

        <div className="label ml-4">Input amount:</div>
        <div className="flex flex-row items-center gap-2">
          <input className={"input w-[25rem]"} type="text" value={tokenAAmount} onChange={(e) => setTokenAAmount(e.target.value)} />
          {!(tokenAAmountRaw ?? -1n >= 0) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="col-span-2 label">Secondary token:</div>

        <div className="label ml-4">Address:</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-[25rem]" type="text" value={tokenBAddress} onChange={(e) => setTokenBAddress(e.target.value as Address)} />
          {!(isAddress(tokenBAddress)) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="label ml-4">Current balance:</div>
        <input className="input w-[25rem]" type="text" value={tokenBBalanceData?.formatted} disabled />

        <div className="label ml-4">Input amount:</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-[25rem]" type="text" value={tokenBAmount} onChange={(e) => setTokenBAmount(e.target.value)} />
          {!(tokenBAmountRaw ?? -1n >= 0) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="col-span-2 my-2 border-t border-green-700"></div>

        <div className="col-span-2 flex flex-row justify-center gap-2">
          {!tokenAApprovalSufficient && <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={erc20Abi}
            action={async ({ estimateAndSend }) => {
              const promise = estimateAndSend("approve", [rewardTokenAddress, tokenAAmountRaw]);
              toast.promise(promise, { pending: 'Approving spending of reward tokens...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to spending of reward tokens: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              tokenAAllowanceRefetch();
              toast.success('Spending of reward tokens finished successfully!');
            }}
            disabled={!(tokenAAmountRaw !== undefined && tokenAAmountRaw >= 0n)}
          >
            Approve {rewardTokenSymbol}
          </Web3Button>}

          {!tokenBApprovalSufficient && <Web3Button
            className="w-48"
            contractAddress={tokenBAddress}
            contractAbi={erc20Abi}
            action={async ({ estimateAndSend }) => {
              const promise = estimateAndSend("approve", [rewardTokenAddress, tokenBAmountRaw]);
              toast.promise(promise, { pending: `Approving ${tokenBSymbol} of secondary tokens...` });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to approve spending of ${tokenBSymbol} tokens: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              tokenBAllowanceRefetch();
              toast.success(`Spending of ${tokenBSymbol} tokens finished successfully!`);
            }}
            disabled={!(tokenBAmountRaw !== undefined && tokenBAmountRaw >= 0n)}
          >
            Approve {tokenBSymbol}
          </Web3Button>}
        </div>

        <div className="label">Slippage tolerance (%):</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-24" type="number" value={slippage} onChange={(e) => setSlippage(e.target.value)} />
          {!(parseFloat(slippage) >= 0) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="col-span-2">
          {estimateError && <div>{estimateError.shortMessage ?? estimateError.name}</div>}
          {estimateData && <div>
            Expected output {formatUnits(estimateData[2], 18)} LP tokens =<br />
            {formatUnits(estimateData[0], 18)} {rewardTokenSymbol} + {formatUnits(estimateData[1], tokenBDecimals)} {tokenBSymbol}
          </div>}
        </div>

        <div className="label">Recipient address:</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-[25rem]" type="text" value={recipient} onChange={(e) => setRecipient(e.target.value as Address)} />
          {!(isAddress(recipient)) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="label">Deadline (minutes):</div>
        <div className="flex flex-row items-center gap-2">
          <input className="input w-24" type="number" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          {!(parseFloat(deadline) > 0) && <div className="text-red-700 text-2xl">⚠</div>}
        </div>

        <div className="col-span-2 flex flex-row justify-end">
          <Web3Button
            className="w-48"
            contractAddress={rewardTokenAddress}
            contractAbi={RewardTokenAbi}
            action={async ({ readContractCustom, estimateAndSend, estimateAndSendCustom }) => {
              const deadlineInSeconds = Math.floor(Date.now() / 1000) + parseInt(deadline) * 60;

              const outputAmountMin = calculateMinAmount(estimateData![0], slippage);
              const secondTokenOutputAmountMin = calculateMinAmount(estimateData![1], slippage);

              const args = [
                dexRouter,
                tokenAAmountRaw,
                tokenBAddress,
                tokenBAmountRaw,
                outputAmountMin,
                secondTokenOutputAmountMin,
                recipient,
                BigInt(deadlineInSeconds),
              ];
              console.log("addLiquidity", args);
              const promise = estimateAndSend("addLiquidity", args, { value: parseUnits(tokenAAmount, 18) });
              toast.promise(promise, { pending: 'Adding liquidity...' });
              return await promise;
            }}
            onError={async (err: any) => {
              console.error(err);
              toast.error(`Failed to add liquidity: ${err.shortMessage ?? err}`);
            }}
            onSuccess={async () => {
              toast.success('Liquidity added successfully!');
              setTokenAAmount('');
              setTokenBAmount('');
            }}
            disabled={!validInput}
          >
            Add Liquidity
          </Web3Button>
        </div>
      </div>
    </div>
  );
}
